import React from 'react';
import styled from 'styled-components';
import { ProductDesign } from '@inter-ikea-storageone/range';
import { capitalizeFirstLetter } from 'products/utils';
import * as R from 'ramda';
import { Product } from '@inter-ikea-storageone/range';
import { useSelector } from 'react-redux';
import { getMarketProducts } from 'ducks/products';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Body from 'ui/Typography/Body';
import Divider from 'ui/Divider/Divider';
import media from 'ui/media';
import { isMobile } from 'utils/platform';

const Wrapper = styled.div`
  width: 100%;
`;

const ColorSwatchGrid = styled.fieldset`
  display: grid;
  border: 0;
  grid-template-rows: max-content;
  height: max-content;
  justify-content: left;
  width: 100%;
  padding: 0;
  &[data-selected='true'] {
    border: 2px solid black;
    padding: 7px;
    box-sizing: content-box;
  }
`;

const ColorSwatchImage = styled.img`
  display: inline-grid;
  justify-items: center;
  align-items: center;
  border-radius: 10%;
  margin-bottom: 0.5rem;
`;

const ColorRadioButton = styled.input``;

const ColorRadioButtonLabel = styled.label`
  [type='radio'] {
    position: absolute;
    width: 1em;
    height: 1em;
    opacity: 0.00001;
  }
  [type='radio'] + img {
    cursor: pointer;
  }
  [type='radio'][data-selected='true'] + img {
    outline: 2px solid ${({ theme }) => theme.colors.feedback.focus};
    outline-offset: 4px;
    border-radius: 10%;
    ${media.mobile`
    outline: 1px solid ${({ theme }) => theme.colors.feedback.focus};
    border-radius: 0;
  `}
  }
`;

type ColorLabel = {
  [value: string]: string;
};

type ChangeColorProps = {
  currentColor: ProductDesign;
  onChangeColor: (design: ProductDesign, index: number) => void;
  allColors: {
    productDesign: ProductDesign;
    image: string;
  }[];
};

const ColorPicker = ({ currentColor, onChangeColor, allColors }: ChangeColorProps) => {
  const products = useSelector(getMarketProducts);
  const uniqueByDesign = R.uniqBy(R.prop('design'));
  const constructDesignLabels = R.reduce((acc, p: Product) => {
    return {
      ...acc,
      [p.design]: p.designText
    };
  }, {});

  const getDesignLabels = R.pipe<Product[], any, any>(uniqueByDesign, constructDesignLabels);

  const colorLabels: ColorLabel = getDesignLabels(products);
  return (
    <Wrapper>
      <>
        {!isMobile() && (
          <Body type={ThemeFontStyleTypeEnum.bodyLarge} mb={'1rem'}>
            {capitalizeFirstLetter(colorLabels[currentColor])}
          </Body>
        )}
      </>
      <ColorSwatchGrid
        role="radiogroup"
        style={{
          gridTemplateColumns: isMobile() ? 'repeat(auto-fit, 4.5rem)' : 'repeat(auto-fit, 5.5rem)'
        }}
      >
        {allColors.map((color, i) => (
          <ColorRadioButtonLabel key={`color-radio-${color.productDesign}`}>
            <ColorRadioButton
              data-testid={`change-color-${color.productDesign}`}
              data-selected={currentColor === color.productDesign}
              id={`color-radio-${color.productDesign}`}
              onChange={() => {
                currentColor !== color.productDesign && onChangeColor(color.productDesign, i);
              }}
              type="radio"
              name="color-radio"
              aria-label={color.productDesign}
              value={color.productDesign}
              checked={currentColor === color.productDesign || false}
            />
            <ColorSwatchImage
              alt={color.productDesign}
              src={color.image}
              style={{ width: isMobile() ? '4rem' : '5rem', height: isMobile() ? '4rem' : '5rem' }}
            />
          </ColorRadioButtonLabel>
        ))}
      </ColorSwatchGrid>
      {isMobile() && (
        <Body type={ThemeFontStyleTypeEnum.bodyLarge} mb={'1rem'}>
          {capitalizeFirstLetter(colorLabels[currentColor])}
        </Body>
      )}
      {!isMobile() && <Divider mt="s" />}
    </Wrapper>
  );
};

export default ColorPicker;
